import { ApiError } from "@/api/client";
import { listIntegrationTokens, revokeIntegrationToken } from "@/api/integration-tokens";
import { CreateIntegrationTokenModal } from "@/components/CreateIntegrationTokenModal";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/modal";
import { Spinner } from "@/components/ui/spinner";
import { EmptyState, ErrorState, LoadingState } from "@/components/ui/states";
import { formatDateBR } from "@/lib/format";
import { TOKEN_STATUS_BADGE_STYLES, TOKEN_STATUS_LABELS, type TokenStatus, UI } from "@/lib/labels";
import { cn } from "@/lib/utils";
import type { IntegrationToken } from "@/types/api";
import { KeyRound, Plus, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

function errorMessage(error: unknown): string {
  if (error instanceof ApiError || error instanceof Error) {
    return error.message;
  }
  return "Erro inesperado. Tente novamente.";
}

/** Deriva a situação do token no cliente (o backend não a envia pronta). */
function tokenStatus(token: IntegrationToken): TokenStatus {
  if (token.revoked_at) {
    return "revogado";
  }
  if (token.expires_at && new Date(token.expires_at).getTime() <= Date.now()) {
    return "expirado";
  }
  return "ativo";
}

const headerClass = "px-3 py-2.5 font-medium";
const cellClass = "px-3 py-2.5 align-top";

/** Gestão de tokens de integração (Personal Access Tokens). Somente admin. */
export function IntegrationTokensPage() {
  const [items, setItems] = useState<IntegrationToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<IntegrationToken | null>(null);
  const [revoking, setRevoking] = useState(false);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await listIntegrationTokens());
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleRevoke = async () => {
    if (!revokeTarget) {
      return;
    }
    setRevoking(true);
    setRevokeError(null);
    try {
      await revokeIntegrationToken(revokeTarget.id);
      setRevokeTarget(null);
      await load();
    } catch (err) {
      setRevokeError(errorMessage(err));
    } finally {
      setRevoking(false);
    }
  };

  const closeRevoke = () => {
    if (!revoking) {
      setRevokeTarget(null);
      setRevokeError(null);
    }
  };

  let content: React.ReactNode;
  if (loading) {
    content = <LoadingState label="Carregando tokens…" />;
  } else if (error) {
    content = <ErrorState message={error} onRetry={() => void load()} />;
  } else if (items.length === 0) {
    content = (
      <EmptyState
        icon={<KeyRound className="h-8 w-8" aria-hidden="true" />}
        title={UI.tokens.emptyTitle}
        description={UI.tokens.emptyDescription}
        action={
          <Button type="button" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            {UI.tokens.newToken}
          </Button>
        }
      />
    );
  } else {
    content = (
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full min-w-[56rem] text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className={headerClass}>{UI.tokens.columns.name}</th>
              <th className={headerClass}>{UI.tokens.columns.creator}</th>
              <th className={headerClass}>{UI.tokens.columns.created}</th>
              <th className={headerClass}>{UI.tokens.columns.expires}</th>
              <th className={headerClass}>{UI.tokens.columns.lastUsed}</th>
              <th className={headerClass}>{UI.tokens.columns.status}</th>
              <th className={cn(headerClass, "text-right")}>{UI.tokens.columns.actions}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/60">
            {items.map((token) => {
              const status = tokenStatus(token);
              return (
                <tr key={token.id} className="transition-colors hover:bg-muted/40">
                  <td className={cellClass}>
                    <p className="font-medium text-foreground">{token.name}</p>
                    <p className="mt-0.5 font-mono text-xs text-muted-foreground">
                      eye_pat_…{token.token_hint}
                    </p>
                  </td>
                  <td className={cellClass}>{token.creator_name}</td>
                  <td className={cn(cellClass, "whitespace-nowrap")}>
                    {formatDateBR(token.created_at)}
                  </td>
                  <td className={cn(cellClass, "whitespace-nowrap")}>
                    {token.expires_at ? formatDateBR(token.expires_at) : UI.tokens.noExpiration}
                  </td>
                  <td className={cn(cellClass, "whitespace-nowrap")}>
                    {token.last_used_at ? formatDateBR(token.last_used_at) : UI.tokens.never}
                  </td>
                  <td className={cellClass}>
                    <Badge className={TOKEN_STATUS_BADGE_STYLES[status]}>
                      {TOKEN_STATUS_LABELS[status]}
                    </Badge>
                  </td>
                  <td className={cn(cellClass, "text-right")}>
                    {status === "ativo" ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setRevokeTarget(token)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" aria-hidden="true" />
                        {UI.tokens.revoke}
                      </Button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-foreground">{UI.tokens.title}</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{UI.tokens.subtitle}</p>
        </div>
        <Button type="button" onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          {UI.tokens.newToken}
        </Button>
      </div>

      {content}

      <CreateIntegrationTokenModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={load}
      />

      <Modal
        open={revokeTarget !== null}
        onClose={closeRevoke}
        title={UI.tokens.revokeConfirmTitle}
        className="max-w-md"
      >
        <div className="space-y-4">
          <p className="text-sm text-foreground">
            Revogar o token <span className="font-medium">{revokeTarget?.name}</span>?
          </p>
          <p className="text-sm text-muted-foreground">{UI.tokens.revokeConfirmWarning}</p>
          {revokeError ? (
            <p className="text-xs text-destructive" role="alert">
              {revokeError}
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={closeRevoke} disabled={revoking}>
              {UI.actions.close}
            </Button>
            <Button type="button" variant="danger" onClick={handleRevoke} disabled={revoking}>
              {revoking ? <Spinner className="h-4 w-4" /> : null}
              {UI.tokens.revoke}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
